import { Divider, Stack, Typography } from '@mui/material';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DynamicFieldRenderer } from '@/shared/components/ui/form/components/dynamic-field-renderer/dynamic-field-renderer';
import {
  TDynamicField,
  TDynamicFieldKind,
} from '@/shared/components/ui/form/components/dynamic-field-renderer/dynamic-field-renderer.types';
import { TCustomFieldsSectionProps } from '@/shared/features/custom-fields/components/custom-fields-section/custom-fields-section.types';
import { useCustomFieldsSection } from '@/shared/features/custom-fields/hooks/use-custom-fields-section/use-custom-fields-section';
import { getCustomFieldFormDefaultValue } from '@/shared/features/custom-fields/utils/custom-field-default-value';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag/use-feature-flag';
import { FEATURE_CUSTOM_FIELDS } from '@/shared/hooks/use-feature-flag/use-feature-flag.constants';
import {
  CustomFieldConfigurationFragment,
  CustomFieldType,
} from '@/shared/types/types.generated';

const getFieldKind = (fieldType: CustomFieldType): TDynamicFieldKind => {
  if (fieldType === CustomFieldType.Number) {
    return 'number';
  }

  if (fieldType === CustomFieldType.Date) {
    return 'date';
  }

  return 'text';
};

export const CustomFieldsSection = <TFieldValues extends FieldValues>({
  assetType,
}: TCustomFieldsSectionProps) => {
  const { t } = useTranslation();
  const { isFeatureFlagEnabled } = useFeatureFlag();
  const isCustomFieldsEnabled = isFeatureFlagEnabled(FEATURE_CUSTOM_FIELDS);

  const { fields, isLoading } = useCustomFieldsSection({
    assetType,
    enabled: isCustomFieldsEnabled,
  });

  if (!isCustomFieldsEnabled || isLoading || !fields.length) {
    return null;
  }

  const toNormalizedField = (
    field: CustomFieldConfigurationFragment,
  ): TDynamicField<TFieldValues> => {
    const name = `customFields.${field.key}` as FieldPath<TFieldValues>;
    const defaultValue = getCustomFieldFormDefaultValue(
      field.fieldType,
      field.defaultValue,
    );

    if (field.fieldType === CustomFieldType.Boolean) {
      return {
        name,
        kind: 'toggle',
        label: field.label,
        required: field.required,
        defaultValue,
        helperText: field.helpText ?? undefined,
      };
    }

    if (field.fieldType === CustomFieldType.SingleSelect) {
      return {
        name,
        kind: 'select',
        label: field.label,
        required: field.required,
        defaultValue,
        hint: field.helpText ?? undefined,
        placeholder: field.placeholder ?? undefined,
        options: field.options.map((option) => ({
          label: option.label ?? option.value,
          value: option.value,
        })),
      };
    }

    if (field.fieldType === CustomFieldType.MultiSelect) {
      return {
        name,
        kind: 'multiSelect',
        label: field.label,
        required: field.required,
        defaultValue,
        options: field.options.map((option) => ({
          label: option.label ?? option.value,
          value: option.value,
        })),
      };
    }

    return {
      name,
      kind: getFieldKind(field.fieldType),
      label: field.label,
      required: field.required,
      defaultValue,
      placeholder: field.placeholder ?? undefined,
      hint: field.helpText ?? undefined,
      multiline: field.fieldType === CustomFieldType.LongText,
    };
  };

  return (
    <Stack gap="1rem">
      <Stack gap="0.75rem">
        <Divider />
        <Typography variant="h7" color="textSecondary">
          {t('custom_fields:title')}
        </Typography>
      </Stack>
      {fields.map((field) => (
        <DynamicFieldRenderer<TFieldValues>
          key={field.fieldId}
          field={toNormalizedField(field)}
        />
      ))}
    </Stack>
  );
};
